import React, {useEffect, useMemo, useState} from 'react'
import {
    Box,
    Card,
    CardContent,
    Chip,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    Typography,
    IconButton,
    Switch,
    FormControlLabel,
} from '@mui/material'
import {Refresh as RefreshIcon} from '@mui/icons-material'
import Button from 'components/Control/Button'
import Table from 'components/Display/Table/Table'
import {
    useClearQueueMutation,
    useGetQueueHealthQuery,
    useGetQueueJobsQuery,
    useGetQueueStatsQuery,
    usePauseQueueMutation,
    useResumeQueueMutation,
} from 'store/Store'
import useQueueJobActions from 'logic/Queue/useQueueJobActions'
import {useTranslation} from 'react-i18next'
import dayjs from 'dayjs'
import {toast} from 'sonner'
import {useUrlParams} from 'services/hooks'

const DEFAULT_STATES = ['waiting', 'active', 'delayed', 'failed', 'completed']

const useNoopMutation = () => {
    return [() => Promise.resolve(), {errorDelete: null}]
}

const QueueSettings = () => {
    const {t} = useTranslation()
    const {urlParams, setUrlParam} = useUrlParams()
    const [autoRefresh, setAutoRefresh] = useState(false)
    const refreshInterval = 5000
    const {data: health, refetch: refetchHealth} = useGetQueueHealthQuery()
    const {
        data: statsResponse,
        isLoading: isStatsLoading,
        refetch: refetchStats,
    } = useGetQueueStatsQuery()
    const [pauseQueue] = usePauseQueueMutation()
    const [resumeQueue] = useResumeQueueMutation()
    const [clearQueue] = useClearQueueMutation()

    const queueNames = useMemo(() => Object.keys(statsResponse || {}), [statsResponse])

    const selectedQueue = urlParams.queueName || ''
    const statesParam = urlParams.states || ''
    const selectedStates = statesParam
        ? statesParam
              .split(',')
              .map((state) => state.trim())
              .filter(Boolean)
        : DEFAULT_STATES
    const stateLabels = useMemo(
        () => ({
            waiting: t('settings.queueManagement.waiting'),
            active: t('settings.queueManagement.active'),
            delayed: t('selectOptions.delayed'),
            failed: t('settings.queueManagement.failed'),
            completed: t('settings.queueManagement.completed'),
        }),
        [t]
    )

    useEffect(() => {
        if (queueNames.length === 0) return
        if (!selectedQueue || !queueNames.includes(selectedQueue)) {
            setUrlParam('queueName', queueNames[0])
        }
    }, [queueNames, selectedQueue, setUrlParam])

    useEffect(() => {
        if (!statesParam || selectedStates.length === 0) {
            setUrlParam('states', DEFAULT_STATES.join(','))
        }
    }, [statesParam, selectedStates.length, setUrlParam])

    useEffect(() => {
        let interval
        if (autoRefresh) {
            interval = setInterval(() => {
                refetchHealth()
                refetchStats()
            }, refreshInterval)
        }
        return () => clearInterval(interval)
    }, [autoRefresh, refreshInterval, refetchHealth, refetchStats])
    const workers = health?.workers || {}

    const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-')

    const getStatusColor = (status) => {
        switch (status) {
            case 'running':
                return 'success'
            case 'paused':
                return 'warning'
            case 'error':
                return 'error'
            default:
                return 'default'
        }
    }

    const onQueueChange = (event) => setUrlParam('queueName', event.target.value)

    const onStatesChange = (event) => {
        const value = event.target.value
        const nextStates = typeof value === 'string' ? value.split(',') : value
        setUrlParam('states', nextStates.join(','))
    }

    const onAction = async (action, queueName) => {
        const actionLabel = t(`settings.queueManagement.actions.${action}`)
        let mutation

        if (action === 'pause') mutation = pauseQueue
        else if (action === 'resume') mutation = resumeQueue
        else mutation = clearQueue

        return toast.promise(
            mutation(queueName)
                .unwrap()
                .then(() => {
                    refetchStats()
                    refetchHealth()
                }),
            {
                loading: t('loading'),
                success: () => t('settings.queueManagement.actionSuccess', {action: actionLabel}),
                error: (err) =>
                    err?.data?.error ||
                    t('settings.queueManagement.actionError', {action: actionLabel}),
            }
        )
    }

    const {jobTableActions} = useQueueJobActions({refetchStats})

    return (
        <Box p={{xs: 1, sm: 2}} width={'100%'}>
            <Stack spacing={2}>
                {/* Header Section */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight="600">
                            {t('settings.queues')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t('settings.queueManagement.jobsTitle')}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="caption">
                                    {t('settings.queueManagement.autoRefresh')}
                                </Typography>
                            }
                        />
                        <IconButton
                            onClick={() => {
                                refetchHealth()
                                refetchStats()
                            }}
                            size="small">
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Quick Status Bar */}
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                        {t('settings.queueManagement.healthTitle')}:
                    </Typography>
                    <Chip
                        size="small"
                        label={health?.status || t('loading')}
                        color={getStatusColor(health?.status)}
                    />
                    <Typography variant="caption" color="textSecondary">
                        {formatDate(health?.timestamp)}
                    </Typography>
                </Stack>

                {/* Queue List */}
                {isStatsLoading ? (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <List disablePadding>
                        {queueNames.map((queueName, index) => {
                            const stats = statsResponse?.[queueName] || {}
                            const worker = workers?.[queueName] || {}
                            const isPaused = Boolean(worker?.isPaused)

                            return (
                                <Box key={queueName}>
                                    <ListItem
                                        disableGutters
                                        sx={{
                                            py: 1,
                                            flexDirection: {xs: 'column', sm: 'row'},
                                            alignItems: {xs: 'flex-start', sm: 'center'},
                                            gap: {xs: 1, sm: 2},
                                        }}>
                                        {/* Name + status */}
                                        <Box sx={{flex: 1, minWidth: 0, width: {xs: '100%', sm: 'auto'}}}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body2" fontWeight={600} noWrap>
                                                    {queueName}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={isPaused
                                                        ? t('settings.queueManagement.statusPaused')
                                                        : t('settings.queueManagement.statusRunning')}
                                                    color={isPaused ? 'warning' : 'success'}
                                                    variant="outlined"
                                                />
                                            </Stack>
                                        </Box>

                                        {/* Stats + Actions row on mobile */}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent={{xs: 'space-between', sm: 'flex-start'}}
                                            sx={{width: {xs: '100%', sm: 'auto'}}}
                                            spacing={{xs: 1, sm: 2}}>
                                            {/* Stats */}
                                            <Stack direction="row" spacing={{xs: 1.5, sm: 3}}>
                                                {[
                                                    {label: t('settings.queueManagement.waiting'), value: stats.waiting || 0, color: 'info.main'},
                                                    {label: t('settings.queueManagement.active'), value: stats.active || 0, color: 'primary.main'},
                                                    {label: t('settings.queueManagement.failed'), value: stats.failed || 0, color: 'error.main'},
                                                    {label: t('settings.queueManagement.completed'), value: stats.completed || 0, color: 'success.main'},
                                                ].map(({label, value, color}) => (
                                                    <Box key={label} textAlign="center" minWidth={28}>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {label}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={700} color={color}>
                                                            {value}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>

                                            {/* Actions */}
                                            <Stack direction="row" spacing={0.5}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={isPaused}
                                                    onClick={() => onAction('pause', queueName)}>
                                                    {t('settings.queueManagement.actions.pause')}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={!isPaused}
                                                    onClick={() => onAction('resume', queueName)}>
                                                    {t('settings.queueManagement.actions.resume')}
                                                </Button>
                                                <Button
                                                    color="error"
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => onAction('clear', queueName)}>
                                                    {t('settings.queueManagement.actions.clear')}
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </ListItem>
                                    {index < queueNames.length - 1 && <Divider />}
                                </Box>
                            )
                        })}
                    </List>
                )}

                {/* Filters */}
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <FormControl
                        fullWidth
                        size="small"
                        disabled={queueNames.length === 0}
                        variant="outlined">
                        <InputLabel id="queue-select-label">
                            {t('settings.queueManagement.selectQueue')}
                        </InputLabel>
                        <Select
                            labelId="queue-select-label"
                            value={selectedQueue}
                            onChange={onQueueChange}
                            label={t('settings.queueManagement.selectQueue')}>
                            {queueNames.length === 0 && (
                                <MenuItem value="">{t('loading')}</MenuItem>
                            )}
                            {queueNames.map((queueName) => (
                                <MenuItem key={queueName} value={queueName}>
                                    {queueName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl
                        fullWidth
                        size="small"
                        disabled={queueNames.length === 0}
                        variant="outlined">
                        <InputLabel id="states-select-label">
                            {t('settings.queueManagement.stateFilter')}
                        </InputLabel>
                        <Select
                            labelId="states-select-label"
                            multiple
                            value={selectedStates}
                            onChange={onStatesChange}
                            label={t('settings.queueManagement.stateFilter')}
                            renderValue={(selected) => (
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {selected.map((state) => (
                                        <Chip
                                            key={state}
                                            size="small"
                                            label={stateLabels[state] || state}
                                        />
                                    ))}
                                </Box>
                            )}>
                            {DEFAULT_STATES.map((state) => (
                                <MenuItem key={state} value={state}>
                                    <Checkbox checked={selectedStates.includes(state)} />
                                    <ListItemText primary={stateLabels[state] || state} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                {!selectedQueue ? (
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="center">
                                <CircularProgress />
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <Table
                        itemName="queue_job"
                        paginationType="external"
                        allowExpanded
                        allowCreate={false}
                        allowEdit={false}
                        allowDelete={false}
                        useGetQuery={useGetQueueJobsQuery}
                        useDeleteMutation={useNoopMutation}
                        tableActions={jobTableActions}
                    />
                )}
            </Stack>
        </Box>
    )
}

export default QueueSettings
